import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegCommentDots } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import { canCorrigerPointages, getStoredUser } from '../../components/Auth/permissions';
import Modal from '../../components/Utils/ModalShow';
import { dateHeureLocale, heureLocale, jourLisible } from './formatPointages';

/**
 * « Historique et anomalies » de l'écran RH Pointages (contrat C23,
 * Yros/Kavero#453).
 *
 * La RH filtre les pointages par période, salarié et anomalie, voit ce qui
 * cloche — sortie manquante, hors zone, pointage corrigé, jour attendu sans
 * pointage — et corrige ou ajoute un pointage. Le motif est obligatoire : la
 * correction s'écrit avec une trace côté Odoo, et nourrit la feuille de temps
 * du salarié.
 *
 * Les heures se lisent et se saisissent dans le fuseau du salarié, que chaque
 * pointage porte, pas dans celui du navigateur. L'état affiché vient toujours d'Odoo : la liste se
 * relit après chaque correction, et son refus s'affiche mot pour mot.
 *
 * Corriger et ajouter sont un droit à part (YRH-CORE-013) : sans lui, la RH lit
 * toujours l'historique et les anomalies, mais n'a plus les boutons ni la
 * colonne d'actions. Le droit se lit dans l'identité du compte (contrat C2),
 * au chargement du portail ; un compte à qui on vient de le retirer garde ses
 * boutons jusqu'au chargement suivant, et seul Odoo le refuse (`non_autorise`).
 */

const CLASSE_CHAMP = 'mt-1 w-full px-2 py-1.5 border rounded-lg text-sm text-slate-700 bg-gray-50 disabled:opacity-60';
const CLASSE_BOUTON = 'rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40';
// Les colonnes de données du tableau. Celle des actions s'y ajoute avec le droit
// de correction : la ligne d'une note s'étend sur toutes, ni plus ni moins. À
// tenir si une colonne s'ajoute ou se retire.
const NB_COLONNES_DONNEES = 6;
const ANOMALIES = ['sortie_manquante', 'hors_zone', 'corrige', 'jour_sans_pointage'];
const COULEUR_ANOMALIE = {
  sortie_manquante: 'bg-red-100 text-red-700',
  hors_zone: 'bg-red-100 text-red-700',
  corrige: 'bg-blue-100 text-blue-800',
  jour_sans_pointage: 'bg-amber-100 text-amber-800',
};

// Le jour local du navigateur, composé sans `toISOString()` : en UTC il
// donnerait la veille ou le lendemain autour de minuit (review 029).
const jourLocal = (decalage = 0) => {
  const repere = new Date();
  repere.setDate(repere.getDate() + decalage);
  const deux = (nombre) => String(nombre).padStart(2, '0');
  return `${repere.getFullYear()}-${deux(repere.getMonth() + 1)}-${deux(repere.getDate())}`;
};

const enMinutes = (heure) => {
  const parties = /^(\d{2}):(\d{2})$/.exec(String(heure ?? ''));
  return parties ? Number(parties[1]) * 60 + Number(parties[2]) : NaN;
};

const interpreterProbleme = (erreur, t) => {
  const enveloppe = erreur?.response?.status === 409 ? erreur.response.data : null;
  return enveloppe?.message || t('pointagesRh.erreurTechnique');
};

const parametres = ({ du, au, employe, anomalie }) => ({
  du,
  au,
  ...(employe ? { employe_id: employe } : {}),
  ...(anomalie ? { anomalie } : {}),
});

function HistoriquePointages() {
  const { t, i18n } = useTranslation();
  const langue = i18n.language;
  const peutCorriger = canCorrigerPointages(getStoredUser());

  const [filtres, setFiltres] = useState(() => ({
    du: jourLocal(-6), au: jourLocal(0), employe: '', anomalie: '',
  }));
  const [donnees, setDonnees] = useState(null);
  const [employes, setEmployes] = useState({});
  const [erreur, setErreur] = useState(null);
  const [saisie, setSaisie] = useState(null);
  const [erreurSaisie, setErreurSaisie] = useState(null);
  const [enCours, setEnCours] = useState(false);

  // La dernière lecture lancée fait foi : la réponse d'une lecture dépassée —
  // un filtre changé entre-temps — est ignorée, succès comme échec.
  const derniereLecture = useRef(0);

  const charger = useCallback(async (aCharger) => {
    const numero = ++derniereLecture.current;
    try {
      const reponse = await apiService.listerPointagesRh(parametres(aCharger));
      if (numero !== derniereLecture.current) return;
      setDonnees(reponse);
      // Les salariés du filtre se choisissent parmi tous ceux déjà vus : une
      // liste filtrée sur l'un d'eux ne doit pas retirer les autres.
      const vus = [
        ...(reponse?.pointages || []).map((pointage) => pointage.employe),
        ...(reponse?.jours_sans_pointage || []).map((jour) => jour.employe),
      ];
      setEmployes((precedents) => vus.reduce(
        (cumul, employe) => ({ ...cumul, [employe.id]: employe.nom }), precedents));
      setErreur(null);
    } catch (probleme) {
      if (numero !== derniereLecture.current) return;
      setErreur(interpreterProbleme(probleme, t));
    }
  }, [t]);

  useEffect(() => { charger(filtres); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changerFiltre = (changement) => {
    const suivants = { ...filtres, ...changement };
    setFiltres(suivants);
    // Odoo exige la période : une date en cours de saisie n'est pas envoyée.
    if (suivants.du && suivants.au) charger(suivants);
  };

  // Chaque pointage porte le fuseau de son salarié : la liste en mêle
  // plusieurs, un fuseau unique fausserait les heures de certains.
  const heure = (pointage, iso, langueAffichage = langue) => heureLocale(iso, pointage.fuseau, langueAffichage);

  const ouvrirCorrection = (pointage) => {
    setErreurSaisie(null);
    setSaisie({
      pointageId: pointage.id,
      employeId: pointage.employe.id,
      employeNom: pointage.employe.nom,
      date: pointage.date,
      entree: heure(pointage, pointage.entree, 'fr') || '',
      sortie: heure(pointage, pointage.sortie, 'fr') || '',
      motif: '',
      fixe: true,
    });
  };

  const ouvrirAjout = (employe, date) => {
    setErreurSaisie(null);
    setSaisie({
      pointageId: null,
      employeId: employe ? employe.id : '',
      employeNom: employe ? employe.nom : '',
      date: date || '',
      entree: '',
      sortie: '',
      motif: '',
      fixe: Boolean(employe),
    });
  };

  const modifierSaisie = (champ, valeur) => setSaisie((precedente) => ({ ...precedente, [champ]: valeur }));

  const enregistrer = async () => {
    if (enCours || !saisie.motif.trim()) return;
    if (!saisie.employeId || !saisie.date) {
      setErreurSaisie(t('pointagesRh.salarieEtDateRequis'));
      return;
    }
    const entree = enMinutes(saisie.entree);
    const sortie = saisie.sortie ? enMinutes(saisie.sortie) : null;
    if (!Number.isFinite(entree) || (saisie.sortie && (!Number.isFinite(sortie) || sortie <= entree))) {
      setErreurSaisie(t('pointagesRh.heuresInvalides'));
      return;
    }
    setErreurSaisie(null);
    setEnCours(true);
    try {
      await apiService.corrigerPointage({
        pointage_id: saisie.pointageId,
        employe_id: Number(saisie.employeId),
        date: saisie.date,
        entree: saisie.entree,
        sortie: saisie.sortie || null,
        motif: saisie.motif.trim(),
      });
    } catch (probleme) {
      setErreurSaisie(interpreterProbleme(probleme, t));
      setEnCours(false);
      return;
    }
    setEnCours(false);
    setSaisie(null);
    await charger(filtres);
  };

  const pointages = donnees?.pointages || [];
  const sansPointage = donnees?.jours_sans_pointage || [];
  const nomsTries = Object.entries(employes).sort(([, gauche], [, droite]) => gauche.localeCompare(droite));

  return (
    <div data-testid="histo-pointages" className="p-4 space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block text-xs text-slate-500">
          {t('pointagesRh.du')}
          <input
            type="date"
            data-testid="histo-du"
            value={filtres.du}
            onChange={(evenement) => changerFiltre({ du: evenement.target.value })}
            className={CLASSE_CHAMP}
          />
        </label>
        <label className="block text-xs text-slate-500">
          {t('pointagesRh.au')}
          <input
            type="date"
            data-testid="histo-au"
            value={filtres.au}
            onChange={(evenement) => changerFiltre({ au: evenement.target.value })}
            className={CLASSE_CHAMP}
          />
        </label>
        <label className="block text-xs text-slate-500">
          {t('pointagesRh.salarie')}
          <select
            data-testid="histo-employe"
            value={filtres.employe}
            onChange={(evenement) => changerFiltre({
              employe: evenement.target.value ? Number(evenement.target.value) : '' })}
            className={CLASSE_CHAMP}
          >
            <option value="">{t('pointagesRh.tousLesSalaries')}</option>
            {nomsTries.map(([id, nom]) => <option key={id} value={id}>{nom}</option>)}
          </select>
        </label>
        <label className="block text-xs text-slate-500">
          {t('pointagesRh.anomalie')}
          <select
            data-testid="histo-anomalie"
            value={filtres.anomalie}
            onChange={(evenement) => changerFiltre({ anomalie: evenement.target.value })}
            className={CLASSE_CHAMP}
          >
            <option value="">{t('pointagesRh.toutesLesAnomalies')}</option>
            {ANOMALIES.map((code) => (
              <option key={code} value={code}>{t(`pointagesRh.anomalies.${code}`)}</option>
            ))}
          </select>
        </label>
      </div>

      {peutCorriger && (
        <div className="flex justify-end">
          <button
            type="button"
            data-testid="pointages-ajouter"
            onClick={() => ouvrirAjout(null, '')}
            className={CLASSE_BOUTON}
          >
            {t('pointagesRh.ajouterPointage')}
          </button>
        </div>
      )}

      {erreur && (
        <p data-testid="histo-erreur" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {erreur}
        </p>
      )}

      {!donnees && !erreur && (
        <p className="text-sm text-slate-400">{t('pointagesRh.chargement')}</p>
      )}

      {donnees && (
        <div data-testid="histo-liste">
          {pointages.length === 0 && sansPointage.length === 0 ? (
            <p data-testid="histo-vide" className="text-sm text-slate-400">{t('pointagesRh.vide')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-xs text-slate-500">
                    <th className="py-2 pr-3 font-medium">{t('pointagesRh.colSalarie')}</th>
                    <th className="py-2 pr-3 font-medium">{t('pointagesRh.colJour')}</th>
                    <th className="py-2 pr-3 font-medium">{t('pointagesRh.colEntree')}</th>
                    <th className="py-2 pr-3 font-medium">{t('pointagesRh.colSortie')}</th>
                    <th className="py-2 pr-3 font-medium">{t('pointagesRh.colSource')}</th>
                    <th className="py-2 pr-3 font-medium">{t('pointagesRh.colAnomalies')}</th>
                    {peutCorriger && <th className="py-2 font-medium" />}
                  </tr>
                </thead>
                <tbody>
                  {pointages.flatMap((pointage) => {
                    const corrige = pointage.source === 'correction_rh';
                    const ligne = (
                      <tr key={pointage.id} data-testid={`histo-ligne-${pointage.id}`} className="border-t border-slate-100">
                        <td className="py-2 pr-3 font-medium text-slate-800">{pointage.employe.nom}</td>
                        <td className="py-2 pr-3 capitalize text-slate-600">{jourLisible(pointage.date, langue)}</td>
                        <td className="py-2 pr-3 text-slate-600">{heure(pointage, pointage.entree) || '—'}</td>
                        <td className="py-2 pr-3 text-slate-600">
                          {heure(pointage, pointage.sortie) || ((pointage.anomalies || []).includes('sortie_manquante') ? '—' : t('pointagesRh.enCoursSortie'))}
                          {pointage.note && (
                            <span
                              data-testid={`histo-note-repere-${pointage.id}`}
                              className="ml-2 inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800"
                            >
                              <FaRegCommentDots className="h-3 w-3" aria-hidden="true" />
                              {t('pointagesRh.avecNote')}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <span
                            data-testid={corrige ? `histo-source-${pointage.id}` : undefined}
                            className={corrige
                              ? 'rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800'
                              : 'text-xs text-slate-500'}
                          >
                            {t(`pointagesRh.sources.${pointage.source}`, { defaultValue: pointage.source })}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <span className="flex flex-wrap gap-1">
                            {(pointage.anomalies || []).map((code) => (
                              <span
                                key={code}
                                data-testid={`histo-anomalie-${pointage.id}-${code}`}
                                className={`rounded-full px-2 py-0.5 text-xs font-medium ${COULEUR_ANOMALIE[code] || 'bg-slate-100 text-slate-700'}`}
                              >
                                {t(`pointagesRh.anomalies.${code}`, { defaultValue: code })}
                              </span>
                            ))}
                          </span>
                        </td>
                        {peutCorriger && (
                          <td className="py-2 text-right">
                            <button
                              type="button"
                              data-testid={`pointage-corriger-${pointage.id}`}
                              onClick={() => ouvrirCorrection(pointage)}
                              className={CLASSE_BOUTON}
                            >
                              {t('pointagesRh.corriger')}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                    if (!pointage.note) return [ligne];

                    // La note, en lecture seule : c'est la parole du salarié, la RH la
                    // lit sans pouvoir la changer. Elle suit la ligne de son pointage.
                    const modifieeLe = dateHeureLocale(pointage.note_modifiee_le, pointage.fuseau, langue);
                    return [ligne, (
                      <tr key={`${pointage.id}-note`} data-testid={`histo-note-ligne-${pointage.id}`}>
                        <td colSpan={NB_COLONNES_DONNEES + (peutCorriger ? 1 : 0)} className="pb-3 pr-3">
                          <div
                            data-testid={`histo-note-${pointage.id}`}
                            className="whitespace-pre-line break-words rounded-lg bg-indigo-50 px-3 py-2 text-sm text-slate-700"
                          >
                            <span className="font-medium text-slate-800">{t('pointagesRh.noteDuSalarie')}</span>
                            {' '}
                            {pointage.note}
                          </div>
                          {modifieeLe && (
                            <p data-testid={`histo-note-modifiee-${pointage.id}`} className="mt-1 text-xs text-slate-500">
                              {t('pointagesRh.noteModifieeLe', modifieeLe)}
                            </p>
                          )}
                        </td>
                      </tr>
                    )];
                  })}
                  {sansPointage.map(({ employe, date }) => (
                    <tr
                      key={`${employe.id}-${date}`}
                      data-testid={`histo-sans-pointage-${employe.id}-${date}`}
                      className="border-t border-slate-100"
                    >
                      <td className="py-2 pr-3 font-medium text-slate-800">{employe.nom}</td>
                      <td className="py-2 pr-3 capitalize text-slate-600">{jourLisible(date, langue)}</td>
                      <td className="py-2 pr-3 text-slate-400">—</td>
                      <td className="py-2 pr-3 text-slate-400">—</td>
                      <td className="py-2 pr-3" />
                      <td className="py-2 pr-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${COULEUR_ANOMALIE.jour_sans_pointage}`}>
                          {t('pointagesRh.anomalies.jour_sans_pointage')}
                        </span>
                      </td>
                      {peutCorriger && (
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            data-testid={`pointage-ajouter-${employe.id}-${date}`}
                            onClick={() => ouvrirAjout(employe, date)}
                            className={CLASSE_BOUTON}
                          >
                            {t('pointagesRh.ajouter')}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={Boolean(saisie)}
        onClose={() => setSaisie(null)}
        title={saisie?.pointageId ? t('pointagesRh.corrigerPointage') : t('pointagesRh.ajouterPointage')}
        footer={saisie && (
          <>
            <button type="button" data-testid="correction-annuler" onClick={() => setSaisie(null)} className={CLASSE_BOUTON}>
              {t('pointagesRh.annuler')}
            </button>
            <button
              type="button"
              data-testid="correction-envoyer"
              onClick={enregistrer}
              disabled={enCours || !saisie.motif.trim()}
              className="rounded-lg bg-[--color-primary] px-4 py-1.5 text-xs font-medium text-white hover:bg-[--bg-hover] disabled:opacity-40"
            >
              {enCours ? t('pointagesRh.enCours') : t('pointagesRh.enregistrer')}
            </button>
          </>
        )}
      >
        {saisie && (
          <div data-testid="correction-modal" className="space-y-3">
            {saisie.fixe ? (
              <p className="text-sm font-medium text-slate-800">{saisie.employeNom}</p>
            ) : (
              <label className="block text-xs text-slate-500">
                {t('pointagesRh.salarie')}
                <select
                  data-testid="correction-employe"
                  value={saisie.employeId}
                  onChange={(evenement) => modifierSaisie('employeId', evenement.target.value)}
                  className={CLASSE_CHAMP}
                >
                  <option value="">{t('pointagesRh.tousLesSalaries')}</option>
                  {nomsTries.map(([id, nom]) => <option key={id} value={id}>{nom}</option>)}
                </select>
              </label>
            )}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <label className="block text-xs text-slate-500">
                {t('pointagesRh.jour')}
                <input
                  type="date"
                  data-testid="correction-date"
                  value={saisie.date}
                  disabled={Boolean(saisie.pointageId) || saisie.fixe}
                  max={jourLocal(0)}
                  onChange={(evenement) => modifierSaisie('date', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </label>
              <label className="block text-xs text-slate-500">
                {t('pointagesRh.entree')}
                <input
                  type="time"
                  data-testid="correction-entree"
                  value={saisie.entree}
                  onChange={(evenement) => modifierSaisie('entree', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </label>
              <label className="block text-xs text-slate-500">
                {t('pointagesRh.sortie')}
                <input
                  type="time"
                  data-testid="correction-sortie"
                  value={saisie.sortie}
                  onChange={(evenement) => modifierSaisie('sortie', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </label>
            </div>
            <p className="text-xs text-slate-500">{t('pointagesRh.sortieAide')}</p>
            <label className="block text-xs text-slate-500">
              {t('pointagesRh.motif')}
              <input
                type="text"
                data-testid="correction-motif"
                value={saisie.motif}
                maxLength={500}
                onChange={(evenement) => modifierSaisie('motif', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </label>
            {erreurSaisie && (
              <p data-testid="correction-erreur" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {erreurSaisie}
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default HistoriquePointages;
